import {Component, DestroyRef, HostListener, inject, OnInit, signal, ViewChild, AfterViewInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationEnd, NavigationError, Router} from '@angular/router';
import {EMPTY, filter, switchMap} from 'rxjs';
import {AnnouncementService} from '../../core/service/announcement.service';
import {AuthService} from '../../core/service/auth.service';
import {AnnouncementAudience, AnnouncementStatus, IAnnouncement} from '../../shared/model/announcement.model';
import {debounce as Debounce} from '../../shared/decorators/debounceDecorator'
import {ModalService} from "../../shared/components/modal/modal.service";
import {CdkScrollable} from '@angular/cdk/scrolling';
import {ScrollTopService} from "../../core/service/scroll-top.service";


@Component({
    selector: 'app-supervisor-landing-page',
    templateUrl: './supervisor-landing-page.component.html',
    styleUrls: ['./supervisor-landing-page.component.scss'],
    standalone: false
})
export class SupervisorLandingPageComponent implements OnInit, AfterViewInit {

  private readonly authService = inject(AuthService);
  private readonly announcementService = inject(AnnouncementService);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly scrollTopService = inject(ScrollTopService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(CdkScrollable, { static: true })
  private routerScroller!: CdkScrollable;

  //TODO: Update to be false once we have made the impersonate bar not always active
  public isImpersonating: boolean | undefined = true;
  public activeAnnouncements: IAnnouncement[] = [];
  public isSideBarOpen: boolean | undefined = undefined;
  public modalIsOpen$ = this.modalService.isModalOpenObservable();

  public isConfidentialityNoticeVisible = signal(false);

  private readonly HAS_CONFIRMED_CONFIDENTIALITY_KEY = 'HasConfirmedConfidentialityNotice'; // 5 seconds

  @HostListener('window:resize')
  @Debounce()
  onResize() {
    this.resetSideBarState();
  }
  private resetSideBarState(): void {
    if (window.innerWidth < 720) return;
    this.isSideBarOpen = undefined;
  }

  ngOnInit() {
    this.listenForSidebarNavigation();
    this.loadAnnouncements();
    this.initializeConfidentialityNotice();
  }

  private listenForSidebarNavigation() {
    this.router.events.pipe(
      filter((event): event is NavigationEnd | NavigationError =>
        event instanceof NavigationEnd || event instanceof NavigationError),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.closeSideBar(true));
  }

  private loadAnnouncements() {
    this.authService.isImpersonating().pipe(
      switchMap((isImpersonating: boolean | undefined) => {
        this.isImpersonating = isImpersonating;

        return this.announcementService.getAdminAnnouncements();
      }),
      switchMap((announcements: IAnnouncement[]) => {
        this.activeAnnouncements = announcements.filter(announcement => {
          return announcement.status === AnnouncementStatus.ACTIVE &&
            (announcement.audience === AnnouncementAudience.GLOBAL || announcement.audience === AnnouncementAudience.SUPERVISOR) &&
            new Date(announcement.startDate) < new Date() &&
            new Date(announcement.endDate) > new Date();
        });

        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private initializeConfidentialityNotice() {
    if(!localStorage.getItem(this.HAS_CONFIRMED_CONFIDENTIALITY_KEY) || localStorage.getItem(this.HAS_CONFIRMED_CONFIDENTIALITY_KEY) === 'false') {
      this.isConfidentialityNoticeVisible.set(true);
    }
  }

  ngAfterViewInit() {
    this.scrollTopService.onRequest()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.scrollToTop());
  }

  private scrollToTop(smooth = true) {
    this.routerScroller.scrollTo({top: 0, behavior: smooth ? 'smooth' : 'auto'});
  }

  public dismissConfidentialityNotice() {
    this.isConfidentialityNoticeVisible.set(false);
    localStorage.setItem(this.HAS_CONFIRMED_CONFIDENTIALITY_KEY, 'true');
  }

  public showConfidentialityNotice() {
    this.isConfidentialityNoticeVisible.set(true);
    localStorage.setItem(this.HAS_CONFIRMED_CONFIDENTIALITY_KEY, 'false');
  }

  public openSideBar(): void {
    this.isSideBarOpen = true;
  }

  public closeSideBar($shouldClose: boolean): void {
    this.isSideBarOpen === undefined || !$shouldClose ? void 0 : this.isSideBarOpen = false;
  }

  public get sideBarState(): string {
    switch (true) {
      case this.isSideBarOpen === undefined:    return '';
      case this.isSideBarOpen === true:         return '--expanded';
      case this.isSideBarOpen === false:        return '--collapsed';
      default:                                  return '';
    }
  }

}
